import { useEffect, useState } from "react";
import { ArrowRight, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../../services/api";

const PromoBar = () => {
  const navigate = useNavigate();

  const [promotion, setPromotion] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    let mounted = true;

    const loadPromoBar = async () => {
      try {
        const promotions = await apiRequest("/storefront/promotions/promo-bar");
        const activePromotion = promotions?.[0];

        if (!mounted) return;

        if (!activePromotion) {
          setPromotion(null);
          setShow(false);
          return;
        }

        const dismissedPromotionId = sessionStorage.getItem(
          "dismissedPromoBarId",
        );

        setPromotion(activePromotion);
        setShow(dismissedPromotionId !== activePromotion.id);
      } catch {
        if (!mounted) return;

        // Promo bar failure must never affect storefront navigation.
        setPromotion(null);
        setShow(false);
      }
    };

    loadPromoBar();

    return () => {
      mounted = false;
    };
  }, []);

  const closePromo = () => {
    if (promotion?.id) {
      sessionStorage.setItem("dismissedPromoBarId", promotion.id);
    }

    setShow(false);
  };

  const handleCta = () => {
    if (!promotion?.ctaUrl) return;

    const isExternalUrl =
      promotion.ctaUrl.startsWith("http://") ||
      promotion.ctaUrl.startsWith("https://");

    if (isExternalUrl) {
      window.location.assign(promotion.ctaUrl);
      return;
    }

    navigate(promotion.ctaUrl);
  };

  return (
    <AnimatePresence initial={false}>
      {show && promotion ? (
        <motion.section
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          className="overflow-hidden bg-black text-white"
          aria-label="Current promotion"
        >
          <div className="relative mx-auto flex min-h-9 max-w-7xl items-center justify-center px-10 py-2 sm:px-14">
            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center">
              <p className="text-[10px] font-semibold uppercase tracking-[0.13em] text-white/90 sm:text-[11px]">
                {promotion.promoBarText}
              </p>

              {promotion.ctaLabel && promotion.ctaUrl ? (
                <button
                  type="button"
                  onClick={handleCta}
                  className="group inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em] text-white transition hover:text-primary-200 sm:text-[11px]"
                >
                  {promotion.ctaLabel}

                  <ArrowRight
                    size={13}
                    className="transition-transform duration-200 group-hover:translate-x-1"
                  />
                </button>
              ) : null}
            </div>

            <button
              type="button"
              onClick={closePromo}
              className="absolute right-3 flex h-7 w-7 items-center justify-center text-white/65 transition hover:text-white sm:right-5"
              aria-label="Dismiss promotion"
            >
              <X size={15} strokeWidth={1.8} />
            </button>
          </div>
        </motion.section>
      ) : null}
    </AnimatePresence>
  );
};

export default PromoBar;